import toast from 'react-hot-toast';
import { agoraChatClient } from '@/features/support-chat/services/agoraChat.client';
import {
  createFiscalDownloadLinkService,
  type FiscalDownloadLinkRequest,
} from '@/shared/services/fiscal.service';
import { getServiceOrigin } from '@/shared/utils/FetchV2.util';
import { logger } from '@/shared/utils/logger';
import { isNativeApp } from '@/shared/utils/nativeBridge';
import { saveBlobWithAnchor } from './anchorDownload';
import { saveRemoteFileAsBlob } from './downloadFile';
import {
  CERTIFICATE_UNAVAILABLE_MESSAGE,
  PREPARE_FILE_ERROR_MESSAGE,
  PREPARING_FILE_MESSAGE,
} from './messages';
import { openRemoteUrl } from './openRemoteUrl';

const CONFLICT_STATUS = 409;

/**
 * A file a backend can hand out as a short-lived link: a fiscal-engine file by
 * `kind` and `ref`, or a chat attachment by the message id in its media path
 * (plus the conversation, for the one that belongs to a ticket).
 */
export type ServerFileTarget =
  | ({ service: 'fiscal' } & FiscalDownloadLinkRequest)
  | { service: 'chat'; messageId: string; conversationId?: string };

type DownloadServerFileOptions = {
  /** Name for the saved file in a browser. */
  fileName: string;
  /**
   * How a normal browser gets the bytes it already has (or can fetch with the session).
   * Without it the browser fetches the public link instead. Never used in the native app.
   */
  fallback?: () => Blob | Promise<Blob>;
};

const serviceOf = (target: ServerFileTarget) =>
  target.service === 'chat' ? ('communications' as const) : ('fiscalEngine' as const);

const publicUrlOf = (target: ServerFileTarget, path: string): string =>
  new URL(path, getServiceOrigin(serviceOf(target))).toString();

const mintLink = (target: ServerFileTarget) =>
  target.service === 'chat'
    ? agoraChatClient.createMediaLink(target.messageId, target.conversationId)
    : createFiscalDownloadLinkService(target);

const errorMessageOf = (target: ServerFileTarget, error: unknown): string => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  const isCertificate =
    target.service === 'fiscal' && target.kind.endsWith('_certificate');
  return isCertificate && status === CONFLICT_STATUS
    ? CERTIFICATE_UNAVAILABLE_MESSAGE
    : PREPARE_FILE_ERROR_MESSAGE;
};

const saveFromFallback = async (
  fallback: () => Blob | Promise<Blob>,
  fileName: string,
): Promise<void> => {
  const produced = fallback();
  saveBlobWithAnchor(produced instanceof Blob ? produced : await produced, fileName);
};

/**
 * Downloads a file the server can build or read on demand.
 *
 * Inside the native app the WebView cannot save a file, so it asks the backend for a
 * 5-minute signed link and hands the https URL to the system browser, which needs no
 * session. In a browser, `fallback` saves the bytes the page already has from the
 * current tab; without it (a file only the server renders: the Zubale certificate,
 * the QR poster) the public link is fetched as a blob and saved the same way.
 *
 * A "Preparando tu archivo..." toast covers the wait; on failure the user sees an
 * error toast and nothing navigates. In an app binary that cannot open the file the
 * toast asks to update the app instead (see `openRemoteUrl`). Resolves to whether the
 * file was handed off, and never throws, so callers can `await` it for their own
 * pending state.
 */
export const downloadServerFile = async (
  target: ServerFileTarget,
  { fileName, fallback }: DownloadServerFileOptions,
): Promise<boolean> => {
  const isNative = isNativeApp();
  if (!isNative && fallback) {
    try {
      await saveFromFallback(fallback, fileName);
      return true;
    } catch (error) {
      logger.error('server_file_fallback', error, { target: target.service });
      toast.error(PREPARE_FILE_ERROR_MESSAGE);
      return false;
    }
  }

  const toastId = toast.loading(PREPARING_FILE_MESSAGE);
  try {
    const { path } = await mintLink(target);
    const publicUrl = publicUrlOf(target, path);
    if (isNative) {
      if (!openRemoteUrl(publicUrl)) {
        // The app is too old for this file and `openRemoteUrl` already said so.
        toast.dismiss(toastId);
        return false;
      }
    } else {
      await saveRemoteFileAsBlob(publicUrl, fileName);
    }
    toast.dismiss(toastId);
    return true;
  } catch (error) {
    logger.error('server_file_download', error, { target: target.service });
    toast.error(errorMessageOf(target, error), { id: toastId });
    return false;
  }
};
